import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Center,
  Stack,
  Text,
  Button,
  SegmentedControl,
  Group,
  Badge,
  Paper,
  LoadingOverlay,
  Title,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconCheck,
  IconX,
  IconWifi,
  IconBattery4,
  IconSignal4g,
} from "@tabler/icons-react";
import { API_BASE_URL } from "../../config/baseUrls";

export default function QuizPreview() {
  const navigate = useNavigate();
  const { id } = useParams();

  // Logic States
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [language, setLanguage] = useState("en");

  // --- LOGIC (UNTOUCHED) ---
  useEffect(() => {
    fetchQuizData();
  }, [id]);

  const fetchQuizData = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/quizzes/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const result = await response.json();
      if (result.success) {
        const quizData = result.data;
        const transformedQuiz = {
          ...quizData,
          questions: quizData.question.map((q) => ({
            ...q,
            options: q.option_option_questionIdToquestion,
            correctAnswerIndex: q.option_option_questionIdToquestion.findIndex(
              (opt) => opt.optionId === q.answerId,
            ),
          })),
        };
        setQuiz(transformedQuiz);
      } else {
        setError("Failed to fetch");
      }
    } catch (err) {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (idx) => {
    setSelectedAnswers((prev) => ({ ...prev, [currentQuestion]: idx }));
  };

  const calculateScore = () => {
    let correct = 0;
    quiz.questions.forEach((q, i) => {
      if (selectedAnswers[i] === q.correctAnswerIndex) correct++;
    });
    return correct;
  };
  // --- LOGIC ENDS ---

  return (
    <Box className="h-full flex flex-col items-center">
      {/* Control Bar */}
      <Group mb="xl" className="w-full max-w-md" justify="space-between">
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={18} />}
          onClick={() => navigate("/admin/dashboard/quiz-management")}
        >
          Exit Preview
        </Button>
        <SegmentedControl
          value={language}
          onChange={setLanguage}
          data={[
            { label: "English", value: "en" },
            { label: "Japanese", value: "ja" },
          ]}
        />
      </Group>

      {/* PHONE FRAME */}
      <Box
        w={350}
        h={700}
        bg="black"
        style={{
          borderRadius: "48px",
          border: "12px solid #1f1f1f",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
        }}
      >
        {/* Dynamic Island */}
        <Box className="absolute top-3 left-1/2 transform -translate-x-1/2 w-24 h-7 bg-black rounded-full z-30" />

        {/* Status Bar */}
        <Group
          justify="space-between"
          px="lg"
          pt="sm"
          className="absolute top-0 w-full text-black z-20"
        >
          <Text size="xs" fw={700}>
            9:41
          </Text>
          <Group gap={4}>
            <IconSignal4g size={12} />
            <IconWifi size={12} />
            <IconBattery4 size={14} />
          </Group>
        </Group>

        {/* SCREEN CONTENT */}
        <Box
          bg="#F8F9FA"
          w="100%"
          h="100%"
          pt={50}
          px="md"
          style={{ overflowY: "auto" }}
        >
          <LoadingOverlay visible={loading} />

          {quiz && !showResults && (
            <Stack gap="lg" h="100%">
              {/* Progress */}
              <Box>
                <Group justify="space-between" mb={4}>
                  <Text size="xs" fw={700} c="dimmed">
                    QUESTION {currentQuestion + 1} / 5
                  </Text>
                  <Badge variant="dot" color="blue">
                    Quiz
                  </Badge>
                </Group>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${((currentQuestion + 1) / 5) * 100}%` }}
                  />
                </div>
              </Box>

              {/* Question */}
              <Title order={4} style={{ lineHeight: 1.3 }}>
                {language === "en"
                  ? quiz.questions[currentQuestion].questionEn
                  : quiz.questions[currentQuestion].questionJa}
              </Title>

              {/* Options */}
              <Stack gap="sm">
                {quiz.questions[currentQuestion].options.map((opt, idx) => (
                  <Paper
                    key={idx}
                    p="md"
                    radius="lg"
                    withBorder
                    onClick={() => handleAnswerSelect(idx)}
                    className={`cursor-pointer transition-all ${
                      selectedAnswers[currentQuestion] === idx
                        ? "bg-blue-50 border-blue-500 ring-1 ring-blue-500"
                        : "hover:bg-gray-50 border-gray-200"
                    }`}
                  >
                    <Group>
                      <Box
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedAnswers[currentQuestion] === idx ? "border-blue-500 bg-blue-500" : "border-gray-300"}`}
                      >
                        {selectedAnswers[currentQuestion] === idx && (
                          <Box className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </Box>
                      <Text size="sm" fw={500}>
                        {language === "en" ? opt.optionEn : opt.optionJa}
                      </Text>
                    </Group>
                  </Paper>
                ))}
              </Stack>

              {/* Nav Buttons */}
              <Group mt="auto" pb="xl">
                {currentQuestion > 0 && (
                  <Button
                    variant="default"
                    flex={1}
                    onClick={() => setCurrentQuestion((c) => c - 1)}
                  >
                    Back
                  </Button>
                )}
                <Button
                  flex={1}
                  color="blue"
                  disabled={selectedAnswers[currentQuestion] === undefined}
                  onClick={() => {
                    if (currentQuestion < 4) setCurrentQuestion((c) => c + 1);
                    else setShowResults(true);
                  }}
                >
                  {currentQuestion === 4 ? "Finish" : "Next"}
                </Button>
              </Group>
            </Stack>
          )}

          {showResults && quiz && (
            <Stack align="center" justify="center" h="80%" gap="xl">
              <Text size="6rem">🎉</Text>
              <Title order={2}>Quiz Complete!</Title>
              <Text size="xl" fw={700}>
                {calculateScore()} / 5 Correct
              </Text>
              <Button
                fullWidth
                onClick={() => {
                  setShowResults(false);
                  setCurrentQuestion(0);
                  setSelectedAnswers({});
                }}
              >
                Try Again
              </Button>
            </Stack>
          )}
        </Box>

        {/* Home Indicator */}
        <Box className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black w-32 h-1 rounded-full" />
      </Box>
    </Box>
  );
}
